<template>
  <v-container class="school-container mt-5">
    <div
      class="school-colour"
      :style="`background-color: ${school.details.colour}`"
    />
    <FullscreenDialog
      :title="$t('mySchool.services.schoolDetails')"
      v-model="schoolDetails"
      :eager="true"
      content-class="school-details"
    >
      <template slot="content">
        <div
          class="school-colour-details"
          :style="`background-color: ${school.details.colour}`"
        />
        <AddGoogleMap
          :location="school.details.campus[0].address"
          :school-name="school.details.name"
        />
        <v-container class="px-6">
          <v-row>
            <v-col>
              <div class="d-flex align-start">
                <div
                  class="school-logo"
                  :style="`border-color: ${school.details.colour}`"
                >
                  <v-img
                    :src="school.details.logo"
                    width="48"
                    height="48"
                    contain
                    class="flex-shrink-0 flex-grow-0"
                  />
                </div>
                <div class="ml-4">
                  <div
                    class="h1 mb-1 font-weight-medium"
                    v-text="school.details.name"
                  />
                  <div>
                    {{ school.details.campus[0].address }}
                  </div>
                </div>
              </div>
            </v-col>
          </v-row>
          <v-row>
            <v-col>
              <v-list>
                <v-list-item
                  class="px-0 mb-5"
                  @click="openMapAddress(school.details.campus[0].address)"
                >
                  <v-list-item-icon class="my-0 mr-4">
                    <v-icon class="success--text">
                      mdi-map-outline
                    </v-icon>
                  </v-list-item-icon>
                  <v-list-item-content class="pa-0">
                    <v-list-item-title>
                      {{ $t("mySchool.details.directions") }}
                    </v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
                <v-list-item
                  class="px-0 mb-5"
                  @click="
                    externalLink(
                      school.details.campus[0].website,
                      'Visit the school website'
                    )
                  "
                >
                  <v-list-item-icon class="my-0 mr-4">
                    <v-icon class="success--text">
                      mdi-school-outline
                    </v-icon>
                  </v-list-item-icon>
                  <v-list-item-content class="pa-0">
                    <v-list-item-title>
                      {{ $t("mySchool.details.website") }}
                    </v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
                <v-list-item
                  class="px-0 mb-5"
                  @click="callNumber(school.details.campus[0].phone)"
                >
                  <v-list-item-icon class="my-0 mr-4">
                    <v-icon class="success--text">
                      mdi-cellphone
                    </v-icon>
                  </v-list-item-icon>
                  <v-list-item-content class="pa-0">
                    <v-list-item-title>
                      {{ $t("mySchool.details.callSchool") }}
                    </v-list-item-title>
                    <!-- <v-list-item-subtitle v-text="school.details.campus[0].phone" /> -->
                  </v-list-item-content>
                </v-list-item>
                <v-list-item
                  class="px-0 mb-5"
                  @click="emailLink(school.details.campus[0].email)"
                >
                  <v-list-item-icon class="my-1 mr-4">
                    <v-icon class="material-icons-outlined success--text">
                      contact_support
                    </v-icon>
                  </v-list-item-icon>
                  <v-list-item-content class="pa-0">
                    <v-list-item-title>
                      {{ $t("mySchool.details.email") }}
                    </v-list-item-title>
                    <!-- <v-list-item-subtitle v-text="school.details.campus[0].email" /> -->
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </v-col>
          </v-row>
        </v-container>
      </template>
    </FullscreenDialog>
    <v-row>
      <v-col>
        <template v-if="school">
          <div class="d-flex mb-6 align-center">
            <div
              class="school-logo"
              :style="`border-color: ${school.details.colour}`"
            >
              <v-img
                :src="school.details.logo"
                width="48"
                height="48"
                contain
                class="flex-shrink-0 flex-grow-0"
              />
            </div>
            <h1
              class="mb-0 ml-4"
              v-text="school.details.name"
            />
          </div>
          <v-list v-if="status">
            <v-subheader class="px-0 success--text">
              {{ $t("mySchool.status.title") }}
            </v-subheader>
            <v-list-item class="px-0 mb-3">
              <v-card class="px-4 py-6 d-flex flex-grow-1 align-center">
                <div class="status-button mr-6">
                  <v-icon
                    v-if="!isOperational(status)"
                    color="orange darken-4"
                  >
                    mdi-alert
                  </v-icon>
                  <v-icon
                    v-if="isOperational(status)"
                    class="ads-success-green"
                  >
                    mdi-check-circle
                  </v-icon>
                  <v-icon v-if="retrieveSchoolStatusError">
                    mdi-help-circle-outline
                  </v-icon>
                </div>
                <div class="font-weight-medium d-flex flex-wrap">
                  <span
                    :class="
                      isOperational(status)
                        ? 'ads-success-green flex-shrink-0 mr-1'
                        : 'orange--text text--darken-4 flex-shrink-0 mr-1'
                    "
                  >{{
                    isOperational(status)
                      ? $t("mySchool.status.operational")
                      : $t("mySchool.status.nonOperational")
                  }}</span>
                  <span class="flex-shrink-0">{{
                    $t("mySchool.status.asOfDate", {
                      date: new Date(status.lastUpdated).toLocaleDateString(
                        locale,
                        {
                          day: "numeric",
                          month: "numeric",
                          year: "numeric"
                        }
                      )
                    })
                  }}</span>
                </div>
              </v-card>
            </v-list-item>
          </v-list>
          <v-list v-else>
            <v-subheader class="px-0 success--text">
              {{ $t("mySchool.status.title") }}
            </v-subheader>
            <v-list-item class="px-0 mb-3">
              <v-card class="px-4 py-6 d-flex flex-grow-1 align-center">
                <div class="status-button mr-6">
                  <v-icon>
                    mdi-help-circle-outline
                  </v-icon>
                </div>
                <div class="font-weight-medium d-flex flex-wrap">
                  {{ $t("mySchool.status.error") }}
                </div>
              </v-card>
            </v-list-item>
          </v-list>
          <v-list>
            <v-subheader class="px-0 success--text">
              {{ $t("mySchool.services.title") }}
            </v-subheader>
            <v-list-item
              class="px-0 mb-3"
              v-if="school.features.payment && school.features.payment.enabled"
            >
              <v-card
                @click="paymentLink(school)"
                class="px-4 py-3 d-flex flex-grow-1 align-center"
              >
                <div class="icon-button payment mr-6">
                  <v-img
                    src="/payment.png"
                    height="48"
                    width="48"
                  />
                </div>
                <div
                  style="font-size: 18px;"
                  class="font-weight-medium"
                >
                  {{ $t("mySchool.services.payment") }}
                </div>
              </v-card>
            </v-list-item>
            <!-- <v-list-item
              class="px-0 mb-3"
            >
              <v-card
                @click="schoolForm = true"
                class="pa-4 d-flex flex-grow-1 align-center"
              >
                <div class="icon-button mr-6">
                  <v-img
                    src="/contact.png"
                    height="41"
                    width="43"
                  />
                </div>
                <div
                  style="font-size: 18px;"
                  class="font-weight-medium"
                >
                  Contact school
                </div>
              </v-card>
            </v-list-item> -->
            <v-list-item class="px-0 mb-3">
              <v-card
                @click="showSchoolDetails()"
                class="px-4 py-3 d-flex flex-grow-1 align-center"
              >
                <div class="icon-button details mr-6">
                  <v-img
                    src="/details.png"
                    height="46"
                    width="46"
                  />
                </div>
                <div
                  style="font-size: 18px;"
                  class="font-weight-medium"
                >
                  {{ $t("mySchool.services.schoolDetails") }}
                </div>
              </v-card>
            </v-list-item>
            <v-list-item class="px-0 mb-3">
              <v-card
                @click="openSchoolVouchers(school)"
                class="px-4 py-3 d-flex flex-grow-1 align-center"
              >
                <div class="mr-6">
                  <v-img
                    src="/img/school-vouchers.svg"
                    height="68"
                    width="68"
                  />
                </div>
                <div
                  style="font-size: 18px;"
                  class="font-weight-medium"
                >
                  {{ $t("mySchool.services.schoolVouchers") }}
                </div>
              </v-card>
            </v-list-item>
          </v-list>
          <v-list v-if="school.essentialApps.length > 0">
            <v-subheader class="px-0 success--text">
              {{ $t("mySchool.services.essentialsTitle") }}
            </v-subheader>
            <v-list-item
              class="px-0 mb-3"
              v-for="(app, index) in school.essentialApps"
              :key="`essential_${index}`"
              @click="externalLink(app.uri, app.title)"
            >
              <v-card class="px-4 py-3 d-flex flex-grow-1 align-center">
                <template v-if="app.icon">
                  <AppIcon
                    :app-name="app.icon"
                    size="68"
                    class="mr-6"
                  />
                </template>
                <template v-else>
                  <div class="icon-button essentials mr-6">
                    <v-img
                      :src="app.iconurl"
                      height="68"
                      width="68"
                    />
                  </div>
                </template>
                <div
                  style="font-size: 18px;"
                  class="font-weight-medium"
                >
                  {{ app.title }}
                </div>
              </v-card>
            </v-list-item>
          </v-list>
          <v-list v-if="school.links.length > 0">
            <v-subheader class="px-0 success--text">
              {{ $t("mySchool.links.title") }}
            </v-subheader>
            <v-list-item
              v-for="(link, index) in school.links"
              class="px-0 mb-5"
              @click="externalLink(link.url, link.name)"
              :key="`link_${index}`"
            >
              <!-- <v-list-item-icon class="my-0 mr-4">
                <v-icon class="ads-blue-1">
                  mdi-open-in-new
                </v-icon>
              </v-list-item-icon>
              <v-list-item-content class="pa-0">
                <v-list-item-title>
                  {{ link.name }}
                </v-list-item-title>
              </v-list-item-content> -->
              <v-card class="px-4 py-3 d-flex flex-grow-1 align-center">
                <div class="icon-button essentials mr-6">
                  <v-img
                    src="/img/school-links.png"
                    height="68"
                    width="68"
                  />
                </div>
                <div
                  style="font-size: 18px;"
                  class="font-weight-medium"
                >
                  {{ link.name }}
                </div>
              </v-card>
            </v-list-item>
          </v-list>
        </template>
      </v-col>
    </v-row>
  </v-container>
</template>
<script>
import {
  FullscreenDialog,
  AdsButton,
  AdsSelect,
  AdsTextField,
  AdsTextArea,
  AppIcon
} from "@nswdoe/doe-ui-core";
import { mapGetters } from "vuex";
import AddGoogleMap from "../components/AddGoogleMap";
import features from "@/features.json";
import {
  FA_SCREEN_CLASS,
  FA_SCREEN_NAME,
  FA_SCREEN_VIEW,
  FA_BUTTON_CLICK
} from "@/settings/constants";
const School = {
  computed: {
    ...mapGetters(["mySchools", "schoolStatus"]),
    school: function() {
      return this.mySchools.find(el => el.schoolId === this.$route.params.id);
    },
    status: function() {
      return this.schoolStatus.results?.find(
        el => parseInt(el.code) === this.school.schoolCode
      );
    },
    retrieveSchoolStatusError: function() {
      return !this.schoolStatus.results;
    },
    locale: function() {
      return this.getLocale() == 'en' ? 'en-au' : this.getLocale()
    }
  },
  components: {
    FullscreenDialog,
    AdsButton,
    AdsSelect,
    AdsTextField,
    AdsTextArea,
    AddGoogleMap,
    AppIcon
  },
  name: "School",
  data() {
    return {
      schoolDetails: false
    };
  },
  created: function() {
    this.$store.dispatch("getSchoolStatus");

    if (parseInt(this.getCookie("appbuild")) >= features.analytics) {
      const school = this.mySchools.find(
        el => el.schoolId === this.$route.params.id
      );
      if (school) {
        this.analytics(FA_SCREEN_VIEW, {
          [FA_SCREEN_CLASS]: "my_schools",
          [FA_SCREEN_NAME]: this.school.details.name.replaceAll(" ", "_")
        });
      }
    }
  },
  methods: {
    callNumber(number) {
      //eslint-disable-next-line
      NativeJS.debugLog(`calling ${number}`);
      //eslint-disable-next-line
      NativeJS.callNumber(number);
    },
    emailLink(email) {
      //eslint-disable-next-line
      NativeJS.debugLog(`sending email to ${email}`);
      //eslint-disable-next-line
      NativeJS.emailLink(email);
    },
    openMapAddress(address) {
      //eslint-disable-next-line
      NativeJS.debugLog(`open address in maps (${address})`);
      //eslint-disable-next-line
      NativeJS.openMapAddress(address);
    },
    externalLink(link, appTitle) {
      this.buttonClickAnalytics(appTitle, this.school);
      //eslint-disable-next-line
      NativeJS.debugLog(`opening external link (${link})`);
      //eslint-disable-next-line
      NativeJS.externalLink(link);
    },
    paymentLink(school) {
      this.setCookie("deeplinkType", "SCHOOL", 3650);
      this.setCookie("deeplinkUrl", school.schoolId, 3650);
      this.buttonClickAnalytics("Make a payment", school);
      //eslint-disable-next-line
      NativeJS.debugLog(`opening payment page for ${school.schoolId}`);
      //eslint-disable-next-line
      NativeJS.showPayment(JSON.stringify(school));
    },
    isOperational(data) {
      if (this.hasPassed(data.operationalDate)) {
        return !data.nonOperational;
      } else {
        return data.nonOperational;
      }
    },
    hasPassed(dateString) {
      let date = new Date(dateString);
      let today = new Date();
      return date <= today;
    },
    showSchoolDetails() {
      this.schoolDetails = true;
      if (parseInt(this.getCookie("appbuild")) >= features.analytics) {
        const school = this.mySchools.find(
          el => el.schoolId === this.$route.params.id
        );
        if (school) {
          this.analytics(FA_SCREEN_VIEW, {
            [FA_SCREEN_CLASS]: "my_schools_details",
            [FA_SCREEN_NAME]: this.school.details.name.replaceAll(" ", "_")
          });
          this.buttonClickAnalytics("School details", school);
        }
      }
    },
    openSchoolVouchers(school) {
      //eslint-disable-next-line
      NativeJS.debugLog("open school vouchers");
      const voucherUrl = this.remoteConfig("backToSchoolVoucherUrl");
      this.setCookie("deeplinkType", "SCHOOL", 3650);
      this.setCookie("deeplinkUrl", school.schoolId, 3650);
      if (parseInt(this.getCookie("appbuild")) >= features.inAppWebPage) {
        //eslint-disable-next-line
        NativeJS.debugLog("open url");
        //eslint-disable-next-line
        NativeJS.openUrl(
          voucherUrl,
          JSON.stringify({
            title: "School Vouchers"
          })
        );
      } else {
        //eslint-disable-next-line
        NativeJS.externalLink(voucherUrl);
      }
    },
    buttonClickAnalytics(buttonText, school) {
      this.analytics(FA_BUTTON_CLICK, {
        category: "my_school",
        label: buttonText.replaceAll(" ", "-"),
        school_name: school.details.name.replaceAll(" ", "_"),
        school_code: `S${school.schoolCode}`
      });
    }
  },
  mounted: function() {
    //eslint-disable-next-line
    NativeJS.debugLog("test debug on school details load");
  }
};
export default School;
</script>

<style lang="scss">
.school-container {
  .school-colour {
    position: absolute;
    height: 8px;
    left: 0;
    top: 0;
    right: 0;
  }
  .toolbar {
    box-shadow: none !important;
  }
  h1 {
    font-size: 1.875rem;
    line-height: 2rem;
    margin-bottom: 1rem;
  }
  .v-subheader {
    font-size: 1.125rem;
    font-weight: 500;
  }
  .v-btn__content {
    line-height: 1;
  }
  .v-label {
    font-size: 1rem !important;
  }
  .school-logo {
    padding: 10px;
    border: 2px solid;
    border-radius: 100px;
  }
  .v-list-item {
    min-height: 0;
  }
  .icon-button {
    border-radius: 15px !important;
    background-color: #cbedfd;
    padding: 13px 9px 14px 16px;
    &.payment {
      padding: 10px;
    }
    &.details {
      padding: 9px 11px 13px;
    }
    &.essentials {
      padding: 0;
    }
  }
  .status-button {
    width: 68px;
    text-align: center;
    flex-shrink: 0;
  }
}
.school-details {
  .toolbar {
    box-shadow: none !important;
  }
  .school-colour-details {
    position: relative;
    height: 8px;
  }
  .h1 {
    font-size: 1.5rem;
    line-height: 2rem;
    margin-bottom: 1rem;
  }
  .school-logo {
    padding: 10px;
    border: 2px solid;
    border-radius: 100px;
  }
  .v-list-item {
    min-height: 0;
  }
  .v-list-item__title {
    font-size: 1.125rem;
    font-weight: 500;
  }
  .v-list-item__subtitle {
    font-size: 1rem;
    font-weight: 500;
    white-space: pre-line;
  }
}
.essential {
  height: 68px !important;
  width: 68px !important;
}
</style>
